<template>
  <f7-page :page-content="false" name="gastos" @page:beforein="cargar">
    <f7-navbar :title="$t('gastos.gastos')" :back-link="$t('atras')">
      <f7-nav-right>
        <!-- El alta empieza por la foto: el botón abre el selector (archivo, galería o cámara en
             la tablet) y el formulario se monta ya con ella y leyéndola. Sin foto no hay gasto. -->
        <f7-link icon-f7="camera_fill" class="gastos-nuevo" :text="$t('gastos.nuevo')" :title="$t('gastos.nuevo')" @click="elegirFoto"></f7-link>
        <!-- Conmutador fichas/tabla: solo en web y para los roles administrativos, que necesitan
             la tabla (Excel, filtros por columna). El resto, y la app Cordova, siempre en fichas. -->
        <f7-link
          v-if="puedeConmutar"
          :icon-f7="modoTablaActivo ? 'square_grid_2x2' : 'list_bullet'"
          :title="modoTablaActivo ? $t('gastos.modo_fichas') : $t('gastos.modo_tabla')"
          @click="conmutarModo"
        ></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <input
      ref="inputFoto"
      type="file"
      accept="image/*,application/pdf"
      style="display: none;"
      @change="fotoElegida"
    />

    <!-- Modo tabla (solo roles administrativos en web): el listado dinámico de siempre. Las columnas
         vienen de la tabla GASTO (colección `tabla`). -->
    <f7-page-content v-if="modoTablaActivo" class="full-height">
      <r-dynamic-list
        ref="rlist"
        :dbAdapter="Gasto"
        :tableCode="'GASTO'"
        :primary="['id']"
        :tableMode="true"
        :virtualListParams="{height:32, rowsBefore:60, rowsAfter:60}"
        class="card full-height data-table"
        classContent="card-content full-height"
        :massiveActions="false"
        :controlsWidth="120"
      >
        <template v-slot:optionsColumn="{item}">
          <f7-link icon-f7="eye" color="green" :href="`/gastos/view/${item.id}/`" v-tooltip="$t('common.ver')"></f7-link>
          <f7-link icon-f7="square_pencil" color="blue" :href="`/gastos/edit/${item.id}/`" v-tooltip="$t('common.editar')"></f7-link>
          <f7-link icon-f7="trash" color="red" @click="$refs.rlist.deleteItem(item.id)" v-tooltip="$t('common.eliminar')"></f7-link>
        </template>
      </r-dynamic-list>
    </f7-page-content>

    <!-- Modo fichas (el de cualquier rol): un mes cada vez. -->
    <!-- `gastos-contenido`: rDynamicList (importado para el modo tabla) trae una regla global
         `.ptr-content { overflow: visible !important }` que dejaba las fichas sin desplazamiento;
         se anula abajo para este contenido. -->
    <f7-page-content v-else ptr class="gastos-contenido" @ptr:refresh="refrescar">
      <!-- Un mes cada vez: se entra en el mes en curso y se navega al anterior y al siguiente. -->
      <div class="gastos-mes">
        <f7-link icon-f7="chevron_left" class="gastos-mes-flecha" @click="mesAnterior" :title="$t('gastos.mes_anterior')"></f7-link>
        <div class="gastos-mes-titulo">
          <span class="gastos-mes-nombre">{{ nombreMes }}</span>
          <f7-link v-if="!esMesActual" class="gastos-mes-hoy" @click="irAlMesActual">{{ $t('gastos.este_mes') }}</f7-link>
        </div>
        <f7-link icon-f7="chevron_right" class="gastos-mes-flecha" @click="mesSiguiente" :title="$t('gastos.mes_siguiente')"></f7-link>
      </div>

      <div v-if="!cargando && !error && gastos.length" class="gastos-resumen">
        <span>{{ gastos.length === 1 ? $t('gastos.un_gasto') : $t('gastos.n_gastos', { n: gastos.length }) }}</span>
        <!-- Un total por moneda: un gasto en dólares no se suma a los euros. -->
        <span class="gastos-resumen-total">{{ $t('gastos.total_mes') }}: <strong>{{ totalesMes.map(function(t) { return formatearImporte(t.total, t.moneda); }).join(' · ') }}</strong></span>
      </div>

      <div v-if="cargando" class="gastos-estado"><f7-preloader></f7-preloader></div>
      <div v-else-if="error" class="gastos-estado gastos-error">
        <p>{{ $t('gastos.error_cargar') }}</p>
        <p class="gastos-error-detalle">{{ error }}</p>
        <f7-button small outline @click="cargar">{{ $t('gastos.reintentar') }}</f7-button>
      </div>
      <div v-else-if="!gastos.length" class="gastos-estado">{{ $t('gastos.sin_gastos_mes', { mes: nombreMes }) }}</div>

      <!-- Fichas: fecha, establecimiento, tipo, categoría y total. Tocar la ficha la abre (y desde
           el detalle se edita); en la ficha solo queda la papelera. -->
      <div v-else class="gastos-fichas">
        <div v-for="g in gastos" :key="g.id" class="card gasto-ficha" @click="ver(g)">
          <div class="gasto-ficha-cabecera">
            <span class="gasto-ficha-fecha">
              {{ formatearFecha(g.fecha) }}<span v-if="g.hora" class="gasto-ficha-hora"> · {{ g.hora }}</span>
            </span>
            <span class="gasto-ficha-importe">{{ formatearImporte(g.importeTotal, g.moneda) }}</span>
          </div>
          <div class="gasto-ficha-establecimiento" :title="g.establecimiento">{{ g.establecimiento || '—' }}</div>
          <div class="gasto-ficha-pie">
            <span class="gasto-chip gasto-chip-tipo">{{ nombreTipo(g.tipoDocumento) }}</span>
            <span class="gasto-chip gasto-chip-categoria">{{ nombreCategoria(g.categoria) }}</span>
            <span v-if="veTodos && g.nombreEmpleado" class="gasto-ficha-empleado">{{ g.nombreEmpleado }}</span>
            <span class="gasto-ficha-acciones" @click.stop>
              <a class="link icon-only color-red" :title="$t('common.eliminar')" @click.stop="eliminar(g)"><i class="icon f7-icons">trash</i></a>
            </span>
          </div>
        </div>
      </div>
    </f7-page-content>
  </f7-page>
</template>

<script>
import rDynamicList from "./../../components/rDynamicList.vue";
import Gasto from "./../../js/db/Gasto.js";
import Utils from "./../../js/Utils.js";
import { capturarFotoBase64, hayCamara, reducirImagen } from "./../../js/CapturaFoto.js";

/** Roles que pueden cambiar a la vista de tabla (Excel, filtros por columna). Solo en web. */
const ROLES_TABLA = ["ADMIN", "ADMINISTRATIVO", "ADMINISTRACION"];
const CLAVE_MODO_TABLA = "gastos.modoTabla";

/**
 * Listado de gastos. Para todos, fichas por meses (fecha, establecimiento, tipo, categoría y
 * total), que es lo que se quiere ver en el móvil y la tablet. Los roles administrativos, en web,
 * pueden conmutar a la tabla de siempre (rDynamicList); en la app Cordova siempre fichas.
 * Quién ve qué gastos lo decide el servidor: sin permiso para ver los ajenos devuelve solo los
 * propios aunque el filtro diga otra cosa.
 */
export default {
  components: {
    rDynamicList
  },
  data() {
    const hoy = new Date();
    return {
      Gasto,
      anio: hoy.getFullYear(),
      mes: hoy.getMonth(), // 0-11
      gastos: [],
      cargando: false,
      error: null,
      numPeticion: 0, // para descartar la respuesta de un mes que ya no se está viendo
      veTodos: false,
      catalogos: { categorias: [], tiposDocumento: [] },
      // Preferencia de vista (solo cuenta si se puede conmutar).
      modoTabla: localStorage.getItem(CLAVE_MODO_TABLA) === "true"
    };
  },
  computed: {
    puedeConmutar: function() {
      if (process.env.TARGET === "cordova") return false;
      const user = Utils.getUser();
      const roles = (user && user.roles) || [];
      return ROLES_TABLA.some(r => roles.includes(r));
    },
    modoTablaActivo: function() {
      return this.puedeConmutar && this.modoTabla;
    },
    nombreMes: function() {
      const nombre = new Date(this.anio, this.mes, 1).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
      return nombre.charAt(0).toUpperCase() + nombre.slice(1);
    },
    esMesActual: function() {
      const hoy = new Date();
      return this.anio === hoy.getFullYear() && this.mes === hoy.getMonth();
    },
    // Suma del mes agrupada por moneda (euros primero): los importes en otra moneda no se mezclan.
    totalesMes: function() {
      const porMoneda = {};
      this.gastos.forEach(function(g) {
        const moneda = /^[A-Z]{3}$/.test(String(g.moneda || "").toUpperCase()) ? String(g.moneda).toUpperCase() : "EUR";
        porMoneda[moneda] = (porMoneda[moneda] || 0) + (Number(g.importeTotal) || 0);
      });
      const euroPrimero = (a, b) => {
        if (a === "EUR") return -1;
        if (b === "EUR") return 1;
        return a.localeCompare(b);
      };
      return Object.keys(porMoneda)
        .sort(euroPrimero)
        .map(moneda => ({ moneda: moneda, total: porMoneda[moneda] }));
    }
  },
  mounted: function() {
    Gasto.catalogos()
      .then(c => {
        this.catalogos = {
          categorias: c.categorias || [],
          tiposDocumento: c.tiposDocumento || []
        };
        this.veTodos = !!c.veTodos;
      })
      .catch(e => {
        console.error("No se han podido cargar los catálogos de gastos", e);
      });
  },
  methods: {
    conmutarModo: function() {
      this.modoTabla = !this.modoTabla;
      localStorage.setItem(CLAVE_MODO_TABLA, String(this.modoTabla));
      if (!this.modoTablaActivo) {
        this.cargar();
      }
    },
    // Se llama al entrar en la página (también al volver de la ficha, para ver lo recién guardado).
    // En modo tabla no hace nada: el listado dinámico se gestiona solo.
    cargar: function() {
      if (this.modoTablaActivo) return Promise.resolve();
      const desde = new Date(this.anio, this.mes, 1);
      const hasta = new Date(this.anio, this.mes + 1, 0, 23, 59, 59, 999);
      const peticion = ++this.numPeticion;
      const filtro = { fechaDesde: desde.toISOString(), fechaHasta: hasta.toISOString() };
      this.cargando = true;
      this.error = null;
      // El mes entero, página a página: la búsqueda pagina en el servidor y un administrador puede
      // tener más gastos en un mes de los que caben en una página; con un corte fijo las fichas y el
      // total del mes se quedarían cortos sin avisar.
      const porPagina = 200, maxPaginas = 50;
      const todos = [];
      const pedirPagina = pagina =>
        Gasto.getList({ filter: filtro, page: pagina, limit: porPagina, order: "fecha", orderDir: "DESC" }, true)
          .then(res => {
            if (peticion !== this.numPeticion) return todos;
            const lista = (res && res.list) || [];
            todos.push(...lista);
            if (lista.length < porPagina || pagina >= maxPaginas) return todos;
            return pedirPagina(pagina + 1);
          });
      return pedirPagina(1)
        .then(lista => {
          if (peticion !== this.numPeticion) return;
          this.gastos = this.ordenar(lista);
          this.cargando = false;
        })
        .catch(e => {
          if (peticion !== this.numPeticion) return;
          this.error = (e && (e.message || e.error)) || String(e);
          this.cargando = false;
        });
    },
    // Tirar hacia abajo para recargar. El callback que cierra el indicador no llega igual en todas
    // partes: `f7-page-content` (Framework7-Vue 5.7) lo pasa como único argumento y `f7-page` como
    // segundo, detrás del elemento. Se busca entre los argumentos y, si no está, se cierra a mano.
    refrescar: function(...args) {
      const done = args.find(a => typeof a === "function");
      const terminar = () => {
        if (done) {
          done();
          return;
        }
        const ptr = this.$el && this.$el.querySelector(".ptr-content");
        if (ptr && this.$f7) this.$f7.ptr.done(ptr);
      };
      this.cargar().then(terminar, terminar);
    },
    // Más reciente primero: fecha, hora y, a igualdad, el último creado.
    ordenar: function(lista) {
      return lista.slice().sort(function(a, b) {
        const fa = new Date(a.fecha).getTime() || 0, fb = new Date(b.fecha).getTime() || 0;
        if (fa !== fb) return fb - fa;
        const ha = a.hora || "", hb = b.hora || "";
        if (ha !== hb) return hb < ha ? -1 : 1;
        return String(b.creationDate || "").localeCompare(String(a.creationDate || ""));
      });
    },
    mesAnterior: function() {
      if (this.mes === 0) {
        this.mes = 11;
        this.anio -= 1;
      } else {
        this.mes -= 1;
      }
      this.cargar();
    },
    mesSiguiente: function() {
      if (this.mes === 11) {
        this.mes = 0;
        this.anio += 1;
      } else {
        this.mes += 1;
      }
      this.cargar();
    },
    irAlMesActual: function() {
      const hoy = new Date();
      this.anio = hoy.getFullYear();
      this.mes = hoy.getMonth();
      this.cargar();
    },
    nombreTipo: function(codigo) {
      return this.nombreCatalogo(this.catalogos.tiposDocumento, codigo);
    },
    nombreCategoria: function(codigo) {
      return this.nombreCatalogo(this.catalogos.categorias, codigo);
    },
    nombreCatalogo: function(lista, codigo) {
      if (!codigo) return "—";
      const entrada = (lista || []).find(function(e) { return e.codigo === codigo; });
      if (entrada && entrada.nombre) return entrada.nombre;
      return codigo.charAt(0) + codigo.slice(1).toLowerCase();
    },
    formatearFecha: function(f) {
      if (!f) return "";
      const d = new Date(f);
      return Number.isNaN(d.getTime()) ? String(f) : d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    /** Importe con la moneda del gasto (la que leyó la IA o la que eligió el empleado), no siempre euros. */
    formatearImporte: function(v, moneda) {
      const codigo = /^[A-Z]{3}$/.test(String(moneda || "").toUpperCase()) ? String(moneda).toUpperCase() : "EUR";
      try {
        return Number(v || 0).toLocaleString("es-ES", { style: "currency", currency: codigo });
      } catch (e) {
        // Código de moneda que el navegador no reconoce: el importe con el código detrás.
        console.warn("Moneda no reconocida al formatear un gasto", codigo, e);
        return Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 }) + " " + codigo;
      }
    },
    ver: function(g) {
      this.$f7router.navigate("/gastos/view/" + g.id + "/");
    },
    eliminar: function(g) {
      const app = this.$f7;
      app.dialog.confirm(this.$t("rlist.pregunta_eliminar_registro"), this.$t("common.eliminar"), () => {
        // Con la versión con la que se listó: si alguien lo editó entre medias, la API contesta
        // conflicto en vez de borrar lo que ese otro acaba de cambiar.
        Gasto.delete({ itemId: g.id, version: g.version })
          .then(res => {
            if (res) {
              app.toast.create({
                icon: '<i class="f7-icons">checkmark</i>',
                text: this.$t("rlist.registro_eliminado"),
                position: "center",
                destroyOnClose: true,
                closeTimeout: 2000
              }).open();
              this.cargar();
            }
          })
          .catch(error => {
            app.dialog.alert(error.message || error.error || error);
          });
      });
    },
    elegirFoto: function() {
      const self = this;
      if (process.env.TARGET === "cordova" && hayCamara()) {
        // En la APK: cámara o archivo (galería, descargas...).
        self.$f7.actions.create({
          buttons: [
            [
              { text: self.$t("gastos.hacer_foto"), bold: true, onClick: function() { self.hacerFoto(); } },
              { text: self.$t("gastos.elegir_archivo"), onClick: function() { self.abrirSelector(); } }
            ],
            [{ text: self.$t("common.cancelar"), color: "red" }]
          ]
        }).open();
        return;
      }
      self.abrirSelector();
    },
    abrirSelector: function() {
      this.$refs.inputFoto.value = "";
      this.$refs.inputFoto.click();
    },
    hacerFoto: function() {
      const self = this;
      const user = Utils.getUser() || {};
      capturarFotoBase64()
        .then(function(foto) {
          if (!foto) return;
          Gasto.fotoPendiente = {
            nombre: foto.nombre,
            extension: foto.extension,
            path: "gastos/" + (user.username || "") + "/",
            // Sin conservar el nombre ni sobrescribir: el core añade el milisegundo al nombre, así que
            // dos fotos con el mismo nombre (IMG_0001.jpg de dos días distintos) no se pisan.
            conservarNombre: false,
            sobrescribir: false,
            _base64: foto._base64
          };
          self.$f7router.navigate("/gastos/new/");
        })
        .catch(function(e) {
          self.$f7.dialog.alert(e && e.message ? e.message : self.$t("gastos.error_leer_fichero"));
        });
    },
    fotoElegida: function(event) {
      const self = this;
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const user = Utils.getUser() || {};
      const reader = new FileReader();
      reader.onload = function(e) {
        // Una foto de móvil son varios MB que la IA no necesita: se reduce antes de guardarla. El
        // PDF u otros ficheros se dejan tal cual.
        reducirImagen(e.target.result).then(function(dataUrl) {
          const esImagenReducida = dataUrl !== e.target.result;
          // Misma forma que deja rFiles al cargar en web: rForm la sube al guardar.
          Gasto.fotoPendiente = {
            nombre: file.name.split(".").slice(0, -1).join(".") || "ticket",
            extension: esImagenReducida ? "jpg" : (/(?:\.([^.]+))?$/.exec(file.name)[1] || "jpg").toLowerCase(),
            path: "gastos/" + (user.username || "") + "/",
            // Sin conservar el nombre ni sobrescribir: el core añade el milisegundo al nombre, así que
            // dos fotos con el mismo nombre (IMG_0001.jpg de dos días distintos) no se pisan.
            conservarNombre: false,
            sobrescribir: false,
            _base64: dataUrl
          };
          self.$f7router.navigate("/gastos/new/");
        });
      };
      reader.onerror = function() {
        self.$f7.dialog.alert(self.$t("gastos.error_leer_fichero"));
      };
      reader.readAsDataURL(file);
    }
  }
};
</script>

<style scoped>
/* Más específica que la `.ptr-content { overflow: visible !important }` global de rDynamicList:
   sin esto el contenido no se desplaza y las fichas que no caben en la pantalla no se alcanzan. */
.gastos-contenido.ptr-content {
  overflow: auto !important;
}
.gastos-mes {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px 4px;
}
.gastos-mes-flecha {
  width: 44px;
  height: 44px;
  justify-content: center;
}
.gastos-mes-titulo {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 180px;
}
.gastos-mes-nombre {
  font-size: 18px;
  font-weight: 600;
}
.gastos-mes-hoy {
  font-size: 13px;
}
.gastos-resumen {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 0 16px 8px;
  color: #666;
  font-size: 14px;
}
.gastos-resumen-total strong {
  color: #000;
}
.gastos-estado {
  padding: 32px 16px;
  text-align: center;
  color: #666;
}
.gastos-error-detalle {
  font-size: 12px;
  color: #999;
}
.gastos-fichas {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;
  padding: 0 12px 16px;
}
.gasto-ficha {
  margin: 0;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.gasto-ficha:active {
  background: #f2f2f4;
}
.gasto-ficha-cabecera {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}
.gasto-ficha-fecha {
  color: #666;
  font-size: 13px;
}
.gasto-ficha-hora {
  color: #999;
}
.gasto-ficha-importe {
  font-size: 18px;
  font-weight: 700;
  white-space: nowrap;
}
.gasto-ficha-establecimiento {
  font-size: 16px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gasto-ficha-pie {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
}
.gasto-chip {
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef0f3;
  color: #444;
  white-space: nowrap;
}
.gasto-chip-tipo {
  background: #e3f0ff;
  color: #1a5fb4;
}
.gasto-ficha-empleado {
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.gasto-ficha-acciones {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
}
.gasto-ficha-acciones a.link {
  margin: 0 4px;
}
@media (max-width: 600px) {
  /* En el móvil el botón de nuevo gasto se queda con la cámara: el texto pisaba el título. */
  .gastos-nuevo ::v-deep span {
    display: none;
  }
  .gastos-fichas {
    grid-template-columns: 1fr;
    padding: 0 8px 16px;
  }
}
</style>
